<template>
  <div class="ds-select" :class="selectClasses">
    <v-select
      v-bind="$attrs"
      :id="computedId"
      :model-value="modelValue"
      @update:model-value="handleInput"
      :items="items"
      :variant="dsVariant"
      :color="dsColor"
      :disabled="disabled"
      :multiple="multiple"
      :chips="chips"
      :clearable="clearable"
      :error-messages="computedErrorMessages"
      :hint="hint"
      :persistent-hint="persistentHint"
      :item-title="itemTitle"
      :item-value="itemValue"
      :label="label"
      class="ds-select__field"
    >
      <template v-for="(_, slot) in $slots" v-slot:[slot]="scope">
        <slot :name="slot" v-bind="scope || {}" />
      </template>
    </v-select>

    <div v-if="helperText && !error" class="ds-select__helper">
      {{ helperText }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

export interface DsSelectProps {
  /**
   * Etiqueta del select
   */
  label?: string
  /**
   * ID del campo (se genera automáticamente si no se proporciona)
   */
  id?: string
  /**
   * Valor del modelo (v-model)
   */
  modelValue?: any
  /**
   * Items del select
   */
  items: any[]
  /**
   * Propiedad del item que se usa como título
   * @default 'title'
   */
  itemTitle?: string
  /**
   * Propiedad del item que se usa como valor
   * @default 'value'
   */
  itemValue?: string
  /**
   * Variante del select
   * @default 'outlined'
   */
  dsVariant?: 'outlined' | 'filled' | 'underlined' | 'plain' | 'solo' | 'solo-inverted' | 'solo-filled'
  /**
   * Color del sistema de diseño
   * @default 'primary'
   */
  dsColor?: 'primary' | 'accent' | 'success' | 'error' | 'warning' | 'info'
  /**
   * Si el campo es requerido
   * @default false
   */
  required?: boolean
  /**
   * Si el campo está deshabilitado
   * @default false
   */
  disabled?: boolean
  /**
   * Si permite selección múltiple
   * @default false
   */
  multiple?: boolean
  /**
   * Si muestra los items seleccionados como chips (para múltiple)
   * @default false
   */
  chips?: boolean
  /**
   * Si se puede limpiar el valor
   * @default false
   */
  clearable?: boolean
  /**
   * Mensaje de error
   */
  error?: string
  /**
   * Texto de ayuda que aparece debajo del campo
   */
  helperText?: string
  /**
   * Hint que aparece dentro del campo
   */
  hint?: string
  /**
   * Si el hint debe ser persistente
   * @default false
   */
  persistentHint?: boolean
  /**
   * Tamaño del label
   * @default 'sm'
   */
  labelSize?: 'xs' | 'sm' | 'base' | 'lg'
  /**
   * Peso del label
   * @default 'medium'
   */
  labelWeight?: 'normal' | 'medium' | 'bold'
}

const props = withDefaults(defineProps<DsSelectProps>(), {
  itemTitle: 'title',
  itemValue: 'value',
  dsVariant: 'outlined',
  dsColor: 'primary',
  required: false,
  disabled: false,
  multiple: false,
  chips: false,
  clearable: false,
  persistentHint: false,
  labelSize: 'sm',
  labelWeight: 'medium'
})

const emit = defineEmits<{
  'update:modelValue': [value: any]
}>()

// Generar ID único si no se proporciona
const computedId = computed(() => {
  return props.id || `ds-select-${Math.random().toString(36).substr(2, 9)}`
})

// Combinar mensajes de error
const computedErrorMessages = computed(() => {
  return props.error ? [props.error] : undefined
})

// Clases del contenedor
const selectClasses = computed(() => ({
  'ds-select--disabled': props.disabled,
  'ds-select--error': !!props.error,
  'ds-select--required': props.required,
  'ds-select--multiple': props.multiple
}))

// Handler para el input
const handleInput = (value: any) => {
  emit('update:modelValue', value)
}
</script>

<style scoped lang="scss">
.ds-select {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;

  &__label {
    margin-bottom: 4px;
  }

  &__field {
    // Los estilos están en los defaults de Vuetify
  }

  &__helper {
    font-size: 0.875rem;
    color: rgb(var(--v-theme-gray-600));
    margin-top: -4px;
    padding-left: 12px;
  }

  &--error &__helper {
    color: rgb(var(--v-theme-error));
  }

  &--disabled {
    opacity: 0.6;
    pointer-events: none;
  }
}
</style>
